import { Fragment, useEffect, useState } from "react";
import { FC } from "react";
import DatePicker from "react-datepicker";
import { Popover, PopoverButton, Transition } from "@headlessui/react";
import ClearDataButton from "../ClearDataButton";
import DatePickerCustomDay from "components/DatePickerCustomDay";
import { changeDate } from "utils/changeDate";
import { useData } from "data/data-provider";
import {
  getFullyBookedDates,
  getUnavailableVehicleDataV2,
} from "api/vehicles/vehicle";
import toast from "react-hot-toast";
import DatePickerCustomHeaderOneMonth from "components/DatePickerCustomHeaderOneMonth";
import moment from "moment";
import { useLocation } from "react-router-dom";

interface UnavailabilityData {
  full_unavailable: string[];
  partial_unavailable: { [key: string]: [string, string][] };
}

export interface RentalCarDatesRangeInputProps {
  className?: string;
  fieldClassName?: string;
  hasButtonSubmit?: boolean;
  selectedCar?: any;
  fleetId?: number;
  isMiddle?: boolean;
  pickupPlaceholder?: string;
  dropoffPlaceholder?: string;
  searchBar?: boolean;
  minDays?: number | null | undefined;
  maxDays?: number | null | undefined;
  bookingDetails?: any;
  variant?: "pickup" | "dropoff";
  hideLabel?: boolean;
  segmentClassName?: string;
  hideIconAtLg?: boolean;
  home?: boolean;
}

const RentalCarDatesRangeInput: FC<RentalCarDatesRangeInputProps> = ({
  className = "",
  fieldClassName = "[ nc-hero-field-padding ]",
  selectedCar,
  fleetId,
  isMiddle,
  searchBar,
  minDays,
  maxDays,
  bookingDetails,
  variant,
  home,
  hideLabel,
  segmentClassName,
  pickupPlaceholder,
  dropoffPlaceholder,
  hideIconAtLg = false,
}) => {
  const {
    startDate,
    endDate,
    setStartDate,
    setEndDate,
    setPartiallyAvailableStartDate,
    setPartiallyAvailableEndDate,
    setPartiallyUnavailableTimes,
    pickupLocation,
    startTime,
    endTime,
  } = useData();

  const [fullyUnavailableDates, setFullyUnavailableDates] = useState<Date[]>(
    [],
  );
  const [fullyBookedDates, setFullyBookedDates] = useState<Date[]>([]);
  const [partialUnavailability, setPartialUnavailability] = useState<{
    [key: string]: { from: string; to: string }[];
  }>({});
  const [customPrices, setCustomPrices] = useState<any[]>([]);
  const [isLargeScreen, setIsLargeScreen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [calendarViewDate, setCalendarViewDate] = useState<Date | null>(null);
  const location = useLocation();

  // Helper for formatting date to "YYYY-MM-DD"
  const formatDate = (date: Date) => moment(date).format("YYYY-MM-DD");

  useEffect(() => {
    const handleResize = () => {
      setIsLargeScreen(window.innerWidth >= 768);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const monthsToShow = isLargeScreen ? 2 : 1;

  const parsePartialUnavailability = (partialUnavailable: {
    [key: string]: [string, string][];
  }) => {
    const result: { [key: string]: { from: string; to: string }[] } = {};

    for (const [dateKey, timeRanges] of Object.entries(partialUnavailable)) {
      result[dateKey] = timeRanges.map(([fromTime, toTime]) => ({
        from: `${fromTime}:00`,
        to: `${toTime}:00`,
      }));
    }

    return result;
  };

  const fetchUnavailableDates = async (startDate: Date, endDate: Date) => {
    if (!selectedCar?.id) {
      console.warn("No car selected, unable to fetch unavailable dates.");
      return;
    }

    setIsLoading(true);
    try {
      const startDateString = new Date(startDate);
      startDateString.setMonth(startDateString.getMonth() - 1);
      const endDateString = new Date(endDate);
      endDateString.setMonth(endDateString.getMonth() + 1);

      const startDateStr = formatDate(startDateString);
      const endDateStr = formatDate(endDateString);
      const response = await getUnavailableVehicleDataV2(
        {
          start_date: startDateStr,
          end_date: endDateStr,
          booking_to_exclude: bookingDetails?.id,
        },
        selectedCar.id,
        pickupLocation?.id,
      );

      if (response.data) {
        const unavailableData = response.data as UnavailabilityData;
        const fullyUnavailable = unavailableData.full_unavailable.map(
          (dateStr) => moment(dateStr).toDate(),
        );
        setFullyUnavailableDates(fullyUnavailable);
        const partialUnavailable = parsePartialUnavailability(
          unavailableData.partial_unavailable,
        );
        setPartialUnavailability(partialUnavailable);
        if (setPartiallyUnavailableTimes) {
          setPartiallyUnavailableTimes(partialUnavailable as any);
        }
      }
    } catch (error) {
      console.error("Error fetching unavailable dates:", error);
      toast.error("Failed to load availability data. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // const fetchFullyBookedDates = async (startDate: Date, endDate: Date) => {
  //   setIsLoading(true);
  //   try {
  //     const startDateString = new Date(startDate);
  //     startDateString.setMonth(startDateString.getMonth() - 1);
  //     const endDateString = new Date(endDate);
  //     endDateString.setMonth(endDateString.getMonth() + 1);

  //     const startDateStr = formatDate(startDateString);
  //     const endDateStr = formatDate(endDateString);
  //     const response = await getFullyBookedDates({
  //       start_date: startDateStr,
  //       end_date: endDateStr,
  //     });

  //     // Parse the fully_booked_dates from response
  //     if (response?.data?.fully_booked_dates) {
  //       const bookedDates = response.data.fully_booked_dates.map(
  //         (dateStr: string) => moment(dateStr).toDate(),
  //       );
  //       setFullyBookedDates(bookedDates);
  //     } else {
  //       setFullyBookedDates([]);
  //     }
  //   } catch (error) {
  //     console.error("Error fetching fully booked dates:", error);
  //     toast.error("Failed to load fully booked dates. Please try again.");
  //   } finally {
  //     setIsLoading(false);
  //   }
  // };

  const isDateSelectable = (date: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const isFullyUnavailable = fullyUnavailableDates.some(
      (unavailableDate) =>
        unavailableDate.toDateString() === date.toDateString(),
    );
    if (isFullyUnavailable) return false;

    // Block fully booked dates only when home is true
    if (home) {
      const isFullyBooked = fullyBookedDates.some(
        (bookedDate) => bookedDate.toDateString() === date.toDateString(),
      );
      if (isFullyBooked) return false;
    }

    if (date < today) return false;

    if (startDate && minDays && maxDays) {
      const minEnd = new Date(startDate);
      minEnd.setDate(startDate.getDate() + minDays - 2);
      const maxEnd = new Date(startDate);
      maxEnd.setDate(startDate.getDate() + maxDays - 1);

      // If selecting end date
      if (date > startDate) {
        if (date < minEnd || date > maxEnd) return false;
      }
    }
    return true;
  };

  const isRangeAvailable = (start: moment.Moment, end: moment.Moment) => {
    const unavailableSet = new Set(
      fullyUnavailableDates.map((date) => moment(date).format("YYYY-MM-DD")),
    );

    let currentDate = start.clone();
    while (currentDate.isSameOrBefore(end, "day")) {
      if (unavailableSet.has(currentDate.format("YYYY-MM-DD"))) {
        return false;
      }
      currentDate.add(1, "day");
    }
    return true;
  };

  const isPartialOverlapValid = (
    start: moment.Moment,
    end: moment.Moment,
    partialUnavailability: Record<string, any>,
  ) => {
    const partialDates = Object.keys(partialUnavailability);

    for (let date of partialDates) {
      const dayMoment = moment(date, "YYYY-MM-DD");
      if (dayMoment.isAfter(start, "day") && dayMoment.isBefore(end, "day")) {
        return false;
      }
    }
    return true;
  };

  const onChangeDate = (dates: [Date | null, Date | null]) => {
    const startMoment = moment(dates[0]);
    const endMoment = moment(dates[1]);
    const isCheck = isRangeAvailable(startMoment, endMoment);
    if (!isCheck) {
      changeDate([null, null], setStartDate, setEndDate, startTime, endTime);
      toast.error("Selected range is fully unavailable.");
      return;
    }

    // Check for fully booked dates when home is true
    if (home) {
      const bookedSet = new Set(
        fullyBookedDates.map((date) => moment(date).format("YYYY-MM-DD")),
      );

      let currentDate = startMoment.clone();
      while (currentDate.isSameOrBefore(endMoment, "day")) {
        if (bookedSet.has(currentDate.format("YYYY-MM-DD"))) {
          changeDate(
            [null, null],
            setStartDate,
            setEndDate,
            startTime,
            endTime,
          );
          toast.error("Selected range contains fully booked dates.");
          return;
        }
        currentDate.add(1, "day");
      }
    }

    const isPartialValid = isPartialOverlapValid(
      startMoment,
      endMoment,
      partialUnavailability,
    );
    if (!isPartialValid) {
      changeDate([null, null], setStartDate, setEndDate, startTime, endTime);
      toast.error(
        "Selected range contains a partially unavailable date in the middle.",
      );
      return;
    }
    changeDate(dates, setStartDate, setEndDate, startTime, endTime);

    const today = new Date();
    const visibleEndDate = new Date(today);
    visibleEndDate.setMonth(today.getMonth() + monthsToShow);
    fetchUnavailableDates(today, visibleEndDate);
  };

  // ---- Initial data fetch for visible months ----
  useEffect(() => {
    const today = new Date();
    const threeMonthsLater = new Date();
    threeMonthsLater.setMonth(today.getMonth() + monthsToShow);
    fetchUnavailableDates(today, threeMonthsLater);
    // fetchCustomPrices(today, threeMonthsLater);
  }, [selectedCar?.id, monthsToShow, pickupLocation]);

  // useEffect(() => {
  //   const today = new Date();
  //   const threeMonthsLater = new Date();
  //   threeMonthsLater.setMonth(today.getMonth() + monthsToShow);
  //   if (home) {
  //     fetchFullyBookedDates(today, threeMonthsLater);
  //   }
  // }, [monthsToShow, home]);

  // ---- Month change handler for DatePicker ----
  const handleMonthChange = (date: Date) => {
    setCalendarViewDate(date);
    const viewStartDate = new Date(date.getFullYear(), date.getMonth(), 1);
    const viewEndDate = new Date(
      date.getFullYear(),
      date.getMonth() + monthsToShow,
      0,
    );
    fetchUnavailableDates(viewStartDate, viewEndDate);
    // if (home) {
    //   fetchFullyBookedDates(viewStartDate, viewEndDate);
    // }
  };

  const renderSearchInput = () => {
    return (
      <>
        <div className="flex-grow text-left ">
          {!hideLabel && (
            <span className="block mt-1 text-xs text-neutral-400 leading-none font-light ">
              {variant === "pickup"
                ? "Pickup Date"
                : variant === "dropoff"
                  ? "Return Date"
                  : "Pickup & Return Date"}
            </span>
          )}

          <div
            className={`${
              hideLabel ? "mt-0" : "mt-1"
            } flex items-center space-x-2`}
          >
            {(variant === undefined || variant === "pickup") && (
              <div
                className={`flex items-center justify-between bg-gradient-to-b from-[#333333] to-[#222222] border border-[#FFFFFF1A] rounded-md px-3 py-2 w-full lg:w-auto md:flex-none md:max-w-none min-w-[80px] xl:min-w-[120px] lg:max-w-[170px] ${
                  segmentClassName ?? ""
                }`}
              >
                <span className="flex items-center text-sm font-normal truncate">
                  <svg
                    className={`w-6 h-4 flex-shrink-0 mr-1 ${
                      hideIconAtLg ? "lg:hidden xl:inline" : ""
                    }`}
                    width="24"
                    height="16"
                    viewBox="0 0 26 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M0 14.5V6H14V14.5C14 15.3438 13.3125 16 12.5 16H1.5C0.65625 16 0 15.3438 0 14.5ZM10 8.375V9.625C10 9.84375 10.1562 10 10.375 10H11.625C11.8125 10 12 9.84375 12 9.625V8.375C12 8.1875 11.8125 8 11.625 8H10.375C10.1562 8 10 8.1875 10 8.375ZM10 12.375V13.625C10 13.8438 10.1562 14 10.375 14H11.625C11.8125 14 12 13.8438 12 13.625V12.375C12 12.1875 11.8125 12 11.625 12H10.375C10.1562 12 10 12.1875 10 12.375ZM6 8.375V9.625C6 9.84375 6.15625 10 6.375 10H7.625C7.8125 10 8 9.84375 8 9.625V8.375C8 8.1875 7.8125 8 7.625 8H6.375C6.15625 8 6 8.1875 6 8.375ZM6 12.375V13.625C6 13.8438 6.15625 14 6.375 14H7.625C7.8125 14 8 13.8438 8 13.625V12.375C8 12.1875 7.8125 12 7.625 12H6.375C6.15625 12 6 12.1875 6 12.375ZM2 8.375V9.625C2 9.84375 2.15625 10 2.375 10H3.625C3.8125 10 4 9.84375 4 9.625V8.375C4 8.1875 3.8125 8 3.625 8H2.375C2.15625 8 2 8.1875 2 8.375ZM2 12.375V13.625C2 13.8438 2.15625 14 2.375 14H3.625C3.8125 14 4 13.8438 4 13.625V12.375C4 12.1875 3.8125 12 3.625 12H2.375C2.15625 12 2 12.1875 2 12.375ZM12.5 2C13.3125 2 14 2.6875 14 3.5V5H0V3.5C0 2.6875 0.65625 2 1.5 2H3V0.5C3 0.25 3.21875 0 3.5 0H4.5C4.75 0 5 0.25 5 0.5V2H9V0.5C9 0.25 9.21875 0 9.5 0H10.5C10.75 0 11 0.25 11 0.5V2H12.5Z"
                      fill="#A9A9A9"
                    />
                  </svg>
                  {startDate?.toLocaleDateString("en-US", {
                    month: "short",
                    day: "2-digit",
                    year: "numeric",
                  }) || pickupPlaceholder}
                </span>
              </div>
            )}
            {(variant === undefined || variant === "dropoff") && (
              <div
                className={`flex items-center justify-between bg-gradient-to-b from-[#333333] to-[#222222] border border-[#FFFFFF1A] rounded-md px-3 py-2 w-full lg:w-auto md:flex-none md:max-w-none min-w-[80px] xl:min-w-[120px] lg:max-w-[170px] ${
                  segmentClassName ?? ""
                }`}
              >
                <span className="flex items-center text-sm font-normal truncate">
                  <svg
                    className={`w-6 h-4 flex-shrink-0 mr-1 ${
                      hideIconAtLg ? "lg:hidden xl:inline" : ""
                    }`}
                    width="24"
                    height="16"
                    viewBox="0 0 26 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M0 14.5V6H14V14.5C14 15.3438 13.3125 16 12.5 16H1.5C0.65625 16 0 15.3438 0 14.5ZM10 8.375V9.625C10 9.84375 10.1562 10 10.375 10H11.625C11.8125 10 12 9.84375 12 9.625V8.375C12 8.1875 11.8125 8 11.625 8H10.375C10.1562 8 10 8.1875 10 8.375ZM10 12.375V13.625C10 13.8438 10.1562 14 10.375 14H11.625C11.8125 14 12 13.8438 12 13.625V12.375C12 12.1875 11.8125 12 11.625 12H10.375C10.1562 12 10 12.1875 10 12.375ZM6 8.375V9.625C6 9.84375 6.15625 10 6.375 10H7.625C7.8125 10 8 9.84375 8 9.625V8.375C8 8.1875 7.8125 8 7.625 8H6.375C6.15625 8 6 8.1875 6 8.375ZM6 12.375V13.625C6 13.8438 6.15625 14 6.375 14H7.625C7.8125 14 8 13.8438 8 13.625V12.375C8 12.1875 7.8125 12 7.625 12H6.375C6.15625 12 6 12.1875 6 12.375ZM2 8.375V9.625C2 9.84375 2.15625 10 2.375 10H3.625C3.8125 10 4 9.84375 4 9.625V8.375C4 8.1875 3.8125 8 3.625 8H2.375C2.15625 8 2 8.1875 2 8.375ZM2 12.375V13.625C2 13.8438 2.15625 14 2.375 14H3.625C3.8125 14 4 13.8438 4 13.625V12.375C4 12.1875 3.8125 12 3.625 12H2.375C2.15625 12 2 12.1875 2 12.375ZM12.5 2C13.3125 2 14 2.6875 14 3.5V5H0V3.5C0 2.6875 0.65625 2 1.5 2H3V0.5C3 0.25 3.21875 0 3.5 0H4.5C4.75 0 5 0.25 5 0.5V2H9V0.5C9 0.25 9.21875 0 9.5 0H10.5C10.75 0 11 0.25 11 0.5V2H12.5Z"
                      fill="#A9A9A9"
                    />
                  </svg>
                  {endDate?.toLocaleDateString("en-US", {
                    month: "short",
                    day: "2-digit",
                    year: "numeric",
                  }) || dropoffPlaceholder}
                </span>
              </div>
            )}
          </div>
        </div>
      </>
    );
  };

  const renderInput = () => {
    return (
      <>
        <div className="text-neutral-300 dark:text-neutral-400">
          <svg
            width="28"
            height="28"
            viewBox="0 0 28 28"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M7.69721 3.42139V5.98717M19.6709 3.42139V5.98717M3.4209 21.3819V8.55296C3.4209 7.87247 3.69122 7.21985 4.1724 6.73868C4.65358 6.2575 5.3062 5.98717 5.98669 5.98717H21.3814C22.0619 5.98717 22.7145 6.2575 23.1957 6.73868C23.6769 7.21985 23.9472 7.87247 23.9472 8.55296V21.3819M3.4209 21.3819C3.4209 22.0624 3.69122 22.715 4.1724 23.1962C4.65358 23.6774 5.3062 23.9477 5.98669 23.9477H21.3814C22.0619 23.9477 22.7145 23.6774 23.1957 23.1962C23.6769 22.715 23.9472 22.0624 23.9472 21.3819M3.4209 21.3819V12.8293C3.4209 12.1488 3.69122 11.4962 4.1724 11.015C4.65358 10.5338 5.3062 10.2635 5.98669 10.2635H21.3814C22.0619 10.2635 22.7145 10.5338 23.1957 11.015C23.6769 11.4962 23.9472 12.1488 23.9472 12.8293V21.3819"
              stroke="white"
              stroke-width="1.71052"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <rect
              x="6.84375"
              y="12.5112"
              width="2.93233"
              height="2.93233"
              rx="0.977442"
              fill="white"
              fill-opacity="0.7"
            />
            <rect
              x="6.84375"
              y="17.3989"
              width="2.93233"
              height="2.93233"
              rx="0.977442"
              fill="white"
              fill-opacity="0.7"
            />
            <rect
              x="11.7305"
              y="12.5112"
              width="2.93233"
              height="2.93233"
              rx="0.977442"
              fill="white"
              fill-opacity="0.7"
            />
            <rect
              x="11.7305"
              y="17.3989"
              width="2.93233"
              height="2.93233"
              rx="0.977442"
              fill="white"
              fill-opacity="0.7"
            />
            <rect
              x="16.6172"
              y="12.5112"
              width="2.93233"
              height="2.93233"
              rx="0.977442"
              fill="white"
              fill-opacity="0.7"
            />
            <rect
              x="16.6172"
              y="17.3989"
              width="2.93233"
              height="2.93233"
              rx="0.977442"
              fill="white"
              fill-opacity="0.7"
            />
          </svg>
        </div>
        <div className="flex-grow text-left">
          <span className="block text-sm font-semibold truncate ">
            {startDate?.toLocaleDateString("en-US", {
              month: "short",
              day: "2-digit",
            }) || "Select Date"}
            {endDate
              ? " - " +
                endDate?.toLocaleDateString("en-US", {
                  month: "short",
                  day: "2-digit",
                })
              : ""}
          </span>
          <span className="block mt-1 text-xs text-white leading-none">
            {"Pickup & Return Date"}
          </span>
        </div>
      </>
    );
  };
  const renderDayContents = (day: number, date?: Date) => {
    if (!date) return day;
    const dateStr = formatDate(date);
    const isFullyUnavailable = fullyUnavailableDates.some(
      (d) => d.toDateString() === date.toDateString(),
    );
    const isPartial = !!partialUnavailability[dateStr];
    const dayPrice = customPrices.find((p) => p.date === dateStr)?.price;
    return (
      <DatePickerCustomDay
        dayOfMonth={day}
        date={date}
        unavailableDates={fullyUnavailableDates}
        partiallyUnavailable={isPartial}
        partialTimes={partialUnavailability[dateStr]}
        home={home}
      />
    );
  };

  return (
    <>
      <Popover
        className={`RentalCarDatesRangeInput relative flex ${className}`}
      >
        {({ open }) => (
          <>
            <div
              className={`flex-1 z-10 flex items-center focus:outline-none ${
                open ? "" : ""
              }`}
            >
              <PopoverButton
                className={`flex-1 flex relative items-center space-x-3 focus:outline-none ${
                  (location.pathname === "/car-listing-detail" ||
                    location.pathname === "/listing-car-detail" ||
                    location.pathname.startsWith("/book/")) &&
                  !searchBar
                    ? "p-3"
                    : "p-0 border-l-0"
                }`}
                onClickCapture={() => document.querySelector("html")?.click()}
              >
                {(location.pathname === "/car-listing-detail" ||
                  location.pathname === "/listing-car-detail" ||
                  location.pathname.startsWith("/book/")) &&
                !searchBar
                  ? renderInput()
                  : renderSearchInput()}

                {((variant === "pickup" && startDate) ||
                  (variant === "dropoff" && endDate) ||
                  (variant === undefined && startDate)) &&
                  open && (
                    <ClearDataButton
                      onClick={() => {
                        if (variant === "pickup") {
                          setStartDate(null);
                          setPartiallyAvailableStartDate(null);
                          setEndDate(null);
                          setPartiallyAvailableEndDate(null);
                        } else if (variant === "dropoff") {
                          setEndDate(null);
                          setPartiallyAvailableEndDate(null);
                        } else {
                          // Clear both if no variant specified
                          setStartDate(null);
                          setEndDate(null);
                          setPartiallyAvailableStartDate(null);
                          setPartiallyAvailableEndDate(null);
                        }
                      }}
                    />
                  )}
              </PopoverButton>
            </div>

            <Transition
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1"
            >
              <Popover.Panel
                className={`absolute z-40 mt-3 top-full md:w-fit max-w-2xl ${
                  !searchBar
                    ? "left-1/2 -translate-x-1/2 md:left-1/2 md:-translate-x-1/2"
                    : "left-full -translate-x-1/2"
                }  lg:max-w-xl`}
              >
                <div
                  className={`overflow-visible min-w-[350px] rounded-xl ${
                    !searchBar ? "" : ""
                  }  shadow-lg ring-1 ring-black ring-opacity-5 bg-white dark:bg-[#000000] border border-[#FFFFFF1A] p-4`}
                >
                  {/* Removed Close (cross) Button */}
                  {isLoading ? (
                    !home && (
                      <div className="pointer-events-none opacity-60 select-none">
                        <div className="relative">
                          <div className="flex items-center space-x-1">
                            <div
                              className="w-6 h-1 rounded border-[0.5px] border-gray-300"
                              style={{ background: "green" }}
                            />
                            <span className="text-xs text-text-color">
                              Available
                            </span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <div
                              className="w-6 h-1 rounded border-[0.5px] border-gray-300"
                              style={{
                                background:
                                  "linear-gradient(to right, #B22222 50%, green 50%)",
                              }}
                            />
                            <span className="text-xs text-text-color">
                              Partially Available
                            </span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <div
                              className="w-6 h-1 rounded border-[0.5px] border-gray-300 line-through"
                              style={{ background: "#B22222" }}
                            />
                            <span className="text-xs text-text-color">
                              Unavailable
                            </span>
                          </div>

                          <DatePicker
                            selected={startDate}
                            onChange={onChangeDate}
                            startDate={startDate}
                            endDate={endDate}
                            selectsRange
                            monthsShown={1}
                            showPopperArrow={false}
                            inline
                            minDate={new Date()}
                            openToDate={
                              calendarViewDate || startDate || new Date()
                            }
                            filterDate={isDateSelectable}
                            renderCustomHeader={(p) => (
                              <>
                                <div className="block  text-text-color">
                                  <DatePickerCustomHeaderOneMonth {...p} />
                                </div>
                              </>
                            )}
                            renderDayContents={renderDayContents}
                            onMonthChange={handleMonthChange}
                          />
                        </div>

                        <div className="absolute left-0 bottom-0 w-full px-8">
                          <div className="h-1 bg-gray-100 rounded-full overflow-hidden relative">
                            <div className="bar-loader h-full bg-blue-400 rounded-full absolute"></div>
                          </div>
                        </div>

                        <div className="absolute inset-0 bg-white opacity-0 pointer-events-auto" />
                      </div>
                    )
                  ) : (
                    <div>
                      <div className="flex flex-col sm:flex-row items-start sm:items-center space-x-0 sm:space-x-4 mb-4">
                        {!home && (
                          <>
                            {" "}
                            <div className="flex items-center space-x-1">
                              <div
                                className="w-6 h-1 rounded border-[0.5px] border-gray-300"
                                style={{ background: "green" }}
                              />
                              <span className="text-xs text-text-color">
                                Available
                              </span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <div
                                className="w-6 h-1 rounded border-[0.5px] border-gray-300"
                                style={{
                                  background:
                                    "linear-gradient(to right, #B22222 50%, green 50%)",
                                }}
                              />
                              <span className="text-xs text-text-color">
                                Partially Available
                              </span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <div
                                className="w-6 h-1 rounded border-[0.5px] border-gray-300 line-through"
                                style={{ background: "#B22222" }}
                              />
                              <span className="text-xs text-text-color">
                                Unavailable
                              </span>
                            </div>
                          </>
                        )}
                      </div>
                      <DatePicker
                        selected={startDate}
                        onChange={onChangeDate}
                        startDate={startDate}
                        endDate={endDate}
                        selectsRange
                        monthsShown={1}
                        showPopperArrow={false}
                        inline
                        minDate={new Date()}
                        openToDate={calendarViewDate || startDate || new Date()}
                        filterDate={isDateSelectable}
                        renderCustomHeader={(p) => (
                          <>
                            <div className="block  text-text-color">
                              <DatePickerCustomHeaderOneMonth {...p} />
                            </div>
                          </>
                        )}
                        renderDayContents={renderDayContents}
                        onMonthChange={handleMonthChange}
                      />
                      <PopoverButton className="mt-6 w-full bg-primary-100 hover:bg-primary-200 text-white font-semibold py-3 rounded-xl transition">
                        Select
                      </PopoverButton>
                    </div>
                  )}
                </div>
              </Popover.Panel>
            </Transition>
          </>
        )}
      </Popover>
    </>
  );
};

export default RentalCarDatesRangeInput;
